import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from '../api.js';
import { checkNewPassword, hasAchievements, MIN_PASSWORD_LENGTH } from '@kraftwerk/shared';
import { currentOwnerId, exportAccountData, useDeleteAccount } from '../auth.js';
import { LogoutButton } from './LogoutButton.js';
import { EncryptionSetting } from './EncryptionSetting.js';
import { ServerView, ServerViewTile } from './ServerView.js';
import {
  addPasskey,
  isAlreadyRegistered,
  isCancelled,
  listPasskeys,
  passkeysSupported,
  removePasskey,
} from '../passkeys.js';
import { db } from '../db/local.js';
import {
  readProfile,
  updateAchievements,
  updateShareWeekStats,
  updateWeeklyReview,
} from '../db/profile.js';
import { pendingPhotoCount, readQuotaFull } from '../db/photos.js';
import type { PushState } from '../push/usePush.js';
import { PrepReminderSetting } from './PrepTile.js';
import { ShortcutSetting } from './ShortcutSetting.js';
import { EnergySetting } from './EnergySetting.js';
import { CalendarSetting } from './CalendarSetting.js';
import { PushSetup } from './PushSetup.js';
import { SystemStatus } from './SystemStatus.js';
import { ThemeToggle } from './ThemeToggle.js';
import { PasswordInput } from './PasswordInput.js';
import { ModulesSetting } from './ModulesSetting.js';
import { WaterSetting } from './nutrition/WaterSetting.js';
import { MealsSetting } from './nutrition/MealsSetting.js';
import { useAchievementScope, useIsChild, useModules } from '../hooks/useModules.js';
import type { Module } from '@kraftwerk/shared';
import { readJournalLock, switchJournalLock } from '../journalLock.js';
import type { UnlockResult } from '../journalLock.js';
import { useSwipeBack } from '../gestures/SwipeProvider.js';
import { RecordTile } from './records/RecordPanel.js';

/**
 * Alles, was man einmal einrichtet und dann selten wieder anfasst.
 *
 * Es stand bis hierher auf „Heute" — sieben Kacheln, von denen morgens eine
 * gebraucht wird. Hinter dem Zahnrad stört es niemanden und ist trotzdem da.
 */
export function SettingsScreen({
  email,
  push,
  focus = null,
  onClose,
  onOpenSetup,
  onSync,
}: {
  email: string;
  push: PushState;
  /** Gleich dorthin scrollen — etwa aus der Einrichtung „Kalender verbinden“. */
  focus?: 'calendar' | null;
  onClose: () => void;
  onOpenSetup?: (() => void) | undefined;
  /** Abgleichrunde — Abmelden gleicht erst ab, bevor es das Gerät leert. */
  onSync?: (() => Promise<unknown>) | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  // Eine Unteransicht: vom linken Rand wischen schließt sie (24.10.2026).
  useSwipeBack(onClose);
  const calendarRef = useRef<HTMLDivElement>(null);
  const modules = useModules();
  const on = (module: Module) => modules.includes(module);
  // Kinder (E16): nichts, was ein Passwort braucht oder Körperzahlen zeigt.
  const child = useIsChild();
  const achievementScope = useAchievementScope();
  const groupRefs = useRef<Partial<Record<SettingsGroup, HTMLElement | null>>>({});
  const me = currentOwnerId();

  useEffect(() => {
    if (focus === 'calendar') calendarRef.current?.scrollIntoView({ block: 'start' });
  }, [focus]);

  // „Was der Server sieht“ ist eine eigene Unteransicht; zurück landet man
  // wieder an ihrer Kachel — oder an der Verschlüsselung, wenn man dorthin wollte.
  const [serverView, setServerView] = useState(false);
  const [returnTo, setReturnTo] = useState<'serverView' | 'encryption' | null>(null);
  const serverViewRef = useRef<HTMLDivElement>(null);
  const encryptionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (serverView || returnTo === null) return;
    const target = returnTo === 'encryption' ? encryptionRef.current : serverViewRef.current;
    target?.scrollIntoView?.({ block: 'start' });
    target?.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
    setReturnTo(null);
  }, [serverView, returnTo]);

  if (serverView) {
    return (
      <ServerView
        onClose={() => {
          setServerView(false);
          setReturnTo('serverView');
        }}
        onSetup={() => {
          setServerView(false);
          setReturnTo('encryption');
        }}
      />
    );
  }

  const group = (key: SettingsGroup, children: React.ReactNode) => (
    <section
      key={key}
      ref={(element) => {
        groupRefs.current[key] = element;
      }}
      aria-labelledby={`settings-group-${key}`}
      className="flex scroll-mt-3 flex-col gap-3"
    >
      <h3
        id={`settings-group-${key}`}
        className="mt-3 font-display text-lg font-bold tracking-wide uppercase"
      >
        {t(`settings.groups.${key}`)}
      </h3>
      {children}
    </section>
  );

  return (
    <section aria-labelledby="settings-heading" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h2 id="settings-heading" className="font-display text-xl font-bold uppercase">
          {t('settings.heading')}
        </h2>
        <button type="button" onClick={onClose} className="kw-btn kw-btn-quiet kw-btn-small px-3">
          {t('settings.close')}
        </button>
      </div>

      {/* Fünfzehn Kacheln untereinander fand niemand wieder. Gruppen mit
          Sprungleiste: Was man sucht, ist zwei Tipper entfernt. */}
      <nav aria-label={t('settings.jump')}>
        <ul className="flex flex-wrap gap-2">
          {SETTINGS_GROUPS.map((key) => (
            <li key={key}>
              <button
                type="button"
                onClick={() => groupRefs.current[key]?.scrollIntoView({ block: 'start' })}
                className="kw-toggle"
              >
                {t(`settings.groups.${key}`)}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {group(
        'reminders',
        <>
          <PushSetup state={push} />
          {on('nutrition') ? (
            <section aria-labelledby="settings-prep" className="kw-tile flex flex-col gap-2">
              <h4 id="settings-prep" className="kw-kicker">
                {t('settings.prep')}
              </h4>
              <PrepReminderSetting />
            </section>
          ) : null}
          {on('nutrition') ? <MealsSetting /> : null}
          {on('nutrition') ? <WaterSetting /> : null}
          <WeeklyReviewSetting />
        </>,
      )}

      {group(
        'me',
        <>
          <ModulesSetting />
          {on('nutrition') ? <EnergySetting /> : null}
          {/* Erfolge: Erwachsene und Teens (28.10.2026), jüngere Kinder nicht. */}
          {hasAchievements(achievementScope) ? <AchievementsSetting child={child} /> : null}
          {/* Akte (27.10.2026), Modul Haushalt — vorerst hier, bis der Raum „Ich“ steht. */}
          {on('household') && me ? (
            <RecordTile
              me={me}
              child={child}
              onSetupEncryption={() => encryptionRef.current?.scrollIntoView?.({ block: 'start' })}
            />
          ) : null}
        </>,
      )}

      {group(
        'connections',
        <>
          {on('planning') && !child ? (
            <div ref={calendarRef} className="scroll-mt-3">
              <CalendarSetting />
            </div>
          ) : null}
          {child ? null : <ShortcutSetting />}
        </>,
      )}

      {group(
        'privacy',
        <>
          <StorageSetting />
          {/* Ende-zu-Ende (E21) — Kinder haben keinen Passkey. */}
          {child ? null : (
            <div ref={encryptionRef} className="scroll-mt-3">
              <EncryptionSetting />
            </div>
          )}
          <div ref={serverViewRef} className="scroll-mt-3">
            <ServerViewTile onOpen={() => setServerView(true)} />
          </div>
          {on('journal') ? <JournalLockSetting /> : null}
        </>,
      )}

      {group(
        'look',
        <section aria-labelledby="settings-look" className="kw-tile flex flex-col gap-2">
          <h4 id="settings-look" className="kw-kicker">
            {t('settings.look')}
          </h4>
          <ThemeToggle />
        </section>,
      )}

      {group(
        'account',
        <>
          {onOpenSetup ? (
            <section aria-labelledby="settings-setup" className="kw-tile flex flex-col gap-2">
              <h4 id="settings-setup" className="kw-kicker">
                {t('setup.reopen')}
              </h4>
              <p className="text-sm text-ink-muted">{t('setup.reopenIntro')}</p>
              <button
                type="button"
                onClick={onOpenSetup}
                className="kw-btn kw-btn-quiet kw-btn-small"
              >
                {t('setup.reopen')}
              </button>
            </section>
          ) : null}
          <AccountSetting email={email} child={child} onSync={onSync} />
          <SystemStatus />
        </>,
      )}
    </section>
  );
}

const SETTINGS_GROUPS = ['reminders', 'me', 'connections', 'privacy', 'look', 'account'] as const;
type SettingsGroup = (typeof SETTINGS_GROUPS)[number];

/**
 * Speicher (E21, 25.10.2026): ein Kontingent für alles — Einträge und Bilder.
 * Fotos gehen immer auf den Server; hier steht nur noch, ob welche warten.
 */
function StorageSetting(): React.JSX.Element {
  const { t } = useTranslation();
  const waiting = useLiveQuery(() => pendingPhotoCount(db), [], 0);

  return (
    <section aria-labelledby="storage-heading" className="kw-tile flex flex-col gap-2">
      <h3 id="storage-heading" className="kw-kicker">
        {t('storage.heading')}
      </h3>
      {waiting > 0 ? (
        <p className="text-xs text-warn">{t('storage.waiting', { count: waiting })}</p>
      ) : null}
      <StorageUsage />
    </section>
  );
}

/**
 * Tagebuch-Sperre je Gerät. Einschalten prüft gleich Face ID — klappt das
 * nicht (kein Passkey dieses Kontos auf dem Gerät), bleibt sie aus, statt das
 * Tagebuch auszusperren. Ausschalten verlangt Entsperren: Sonst schaltet sie
 * ab, wer das Telefon gerade in der Hand hat.
 */
function JournalLockSetting(): React.JSX.Element {
  const { t } = useTranslation();
  const on = useLiveQuery(() => readJournalLock(db), [], null);
  const [supported] = useState(passkeysSupported);
  const [result, setResult] = useState<UnlockResult | null>(null);
  const [busy, setBusy] = useState(false);

  async function toggle(next: boolean) {
    setResult(null);
    setBusy(true);
    const outcome = await switchJournalLock(next);
    setBusy(false);
    if (outcome !== 'unlocked') setResult(outcome);
  }

  return (
    <section aria-labelledby="settings-journal-lock" className="kw-tile flex flex-col gap-2">
      <h3 id="settings-journal-lock" className="kw-kicker">
        {t('journalLock.heading')}
      </h3>
      <p className="text-sm text-ink-muted">{t('journalLock.intro')}</p>
      {!supported ? (
        <p className="text-sm text-ink-muted">{t('journalLock.unsupported')}</p>
      ) : on === null ? null : (
        <button
          type="button"
          aria-pressed={on}
          disabled={busy}
          onClick={() => void toggle(!on)}
          className={`kw-btn kw-btn-small ${on ? '' : 'kw-btn-quiet'}`}
        >
          {on ? t('journalLock.on') : t('journalLock.off')}
        </button>
      )}
      {result ? (
        <p role="status" className="text-sm text-warn">
          {t(`journalLock.result.${result}`)} {t('journalLock.needPasskey')}
        </p>
      ) : null}
    </section>
  );
}

/** Konto: wer angemeldet ist, Abmelden, Passwort, Passkeys, Export, Löschen. */
function AccountSetting({
  email,
  child,
  onSync,
}: {
  email: string;
  child: boolean;
  onSync?: (() => Promise<unknown>) | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <section aria-labelledby="account-heading" className="kw-tile flex flex-col gap-3">
      <div>
        <h3 id="account-heading" className="kw-kicker">
          {t('account.heading')}
        </h3>
        <p className="mt-1 text-sm break-all">
          {/* Kinder haben keine E-Mail — nur eine Platzhalter-Adresse. */}
          {child ? t('session.signedInChild') : t('session.signedInAs', { email })}
        </p>
      </div>

      <LogoutButton onSync={onSync} />

      {child ? null : <ChangePassword />}
      {child ? null : <PasskeySetting />}
      <ExportData />
      {child ? null : <DeleteAccount />}
    </section>
  );
}

/**
 * Passwort ändern — mit dem bisherigen. Danach sind alle **anderen** Geräte
 * abgemeldet; das sagt der Text vorher, damit es niemanden überrascht.
 */
function ChangePassword(): React.JSX.Element {
  const { t } = useTranslation();
  const ids = { current: useId(), next: useId(), repeat: useId() };
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const reset = () => {
    setCurrent('');
    setNext('');
    setRepeat('');
  };

  if (!open) {
    return (
      <div className="flex flex-col gap-1 border-t border-line pt-3">
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setResult(null);
          }}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('password.change')}
        </button>
        {result?.ok ? (
          <p role="status" className="text-sm text-good">
            {result.text}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-2 border-t border-line pt-3"
      onSubmit={(event) => {
        event.preventDefault();
        const problem = checkNewPassword(next, repeat, current);
        if (problem) {
          setResult({
            ok: false,
            text: t(`password.problem.${problem}`, { min: MIN_PASSWORD_LENGTH }),
          });
          return;
        }
        setBusy(true);
        void apiFetch<undefined>('/account/password', {
          method: 'POST',
          body: JSON.stringify({ current, next }),
        })
          .then(() => {
            reset();
            setOpen(false);
            setResult({ ok: true, text: t('password.changed') });
          })
          .catch((error: unknown) => {
            const status = error instanceof ApiError ? error.status : 0;
            setResult({
              ok: false,
              text:
                status === 403
                  ? t('settings.delete.wrongPassword')
                  : status === 429
                    ? t('login.tooMany')
                    : t('password.failed'),
            });
          })
          .finally(() => setBusy(false));
      }}
    >
      <h4 className="kw-label">{t('password.change')}</h4>
      <p className="text-xs text-ink-muted">{t('password.changeHint')}</p>
      <label htmlFor={ids.current} className="flex flex-col gap-1">
        <span className="kw-label">{t('password.current')}</span>
        <PasswordInput
          id={ids.current}
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          className="kw-field"
          required
        />
      </label>
      <label htmlFor={ids.next} className="flex flex-col gap-1">
        <span className="kw-label">{t('password.next', { min: MIN_PASSWORD_LENGTH })}</span>
        <PasswordInput
          id={ids.next}
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          className="kw-field"
          required
        />
      </label>
      <label htmlFor={ids.repeat} className="flex flex-col gap-1">
        <span className="kw-label">{t('password.repeat')}</span>
        <PasswordInput
          id={ids.repeat}
          autoComplete="new-password"
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          className="kw-field"
          required
        />
      </label>
      {result && !result.ok ? (
        <p role="alert" className="text-sm text-danger">
          {result.text}
        </p>
      ) : null}
      <button type="submit" disabled={busy} className="kw-btn kw-btn-small">
        {busy ? t('password.busy') : t('password.save')}
      </button>
      <button
        type="button"
        onClick={() => {
          reset();
          setResult(null);
          setOpen(false);
        }}
        className="kw-btn kw-btn-quiet kw-btn-small"
      >
        {t('nutrition.library.cancel')}
      </button>
    </form>
  );
}

/** Wochenrückblick sonntags ab 18:00 als Mitteilung — an oder aus. */
function WeeklyReviewSetting(): React.JSX.Element {
  const { t } = useTranslation();
  const [on, setOn] = useState<boolean | null>(null);

  useEffect(() => {
    void readProfile(db).then((profile) => setOn(profile.weeklyReview));
  }, []);

  return (
    <section aria-labelledby="settings-review" className="kw-tile flex flex-col gap-2">
      <h3 id="settings-review" className="kw-kicker">
        {t('settings.review.heading')}
      </h3>
      <p className="text-sm text-ink-muted">{t('settings.review.intro')}</p>
      {on === null ? null : (
        <button
          type="button"
          aria-pressed={on}
          onClick={() => {
            const next = !on;
            setOn(next);
            void updateWeeklyReview(next, db);
          }}
          className={`kw-btn kw-btn-small ${on ? '' : 'kw-btn-quiet'}`}
        >
          {on ? t('settings.review.on') : t('settings.review.off')}
        </button>
      )}
    </section>
  );
}

const PASSKEYS_QUERY_KEY = ['passkeys'] as const;

/**
 * Passkeys: Face ID statt Passwort. Hinzufügen verlangt einmal das Passwort —
 * sonst könnte sich jeder mit dem entsperrten Telefon in der Hand einen eigenen,
 * dauerhaften Zugang eintragen.
 */
function PasskeySetting(): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const passwordId = useId();
  const nameId = useId();
  const [supported] = useState(passkeysSupported);
  const [adding, setAdding] = useState(false);
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const list = useQuery({
    queryKey: PASSKEYS_QUERY_KEY,
    queryFn: listPasskeys,
    enabled: supported,
  });
  const add = useMutation({
    mutationFn: addPasskey,
    onSuccess: async () => {
      setAdding(false);
      setPassword('');
      setName('');
      await queryClient.invalidateQueries({ queryKey: PASSKEYS_QUERY_KEY });
    },
  });
  const remove = useMutation({
    mutationFn: removePasskey,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PASSKEYS_QUERY_KEY }),
  });

  if (!supported) return null;

  const date = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  const addStatus = add.error instanceof ApiError ? add.error.status : null;
  const addError = !add.isError
    ? null
    : isCancelled(add.error)
      ? null
      : isAlreadyRegistered(add.error)
        ? t('passkeys.exists')
        : addStatus === 403
          ? t('settings.delete.wrongPassword')
          : t('passkeys.failed');

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-3">
      <h4 className="kw-label">{t('passkeys.heading')}</h4>
      <p className="text-xs text-ink-muted">{t('passkeys.intro')}</p>

      {list.isError ? (
        <p role="status" className="text-xs text-ink-muted">
          {t('passkeys.offline')}
        </p>
      ) : list.data && list.data.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {list.data.map((key) => (
            <li
              key={key.id}
              className="flex items-center justify-between gap-2 rounded-xl border border-line px-3 py-2 text-sm"
            >
              <span className="min-w-0">
                <span className="block truncate font-semibold">{key.name}</span>
                <span className="block text-xs text-ink-muted">
                  {key.lastUsedAt
                    ? t('passkeys.lastUsed', { date: date(key.lastUsedAt) })
                    : t('passkeys.created', { date: date(key.createdAt) })}
                </span>
              </span>
              <button
                type="button"
                onClick={() => remove.mutate(key.id)}
                disabled={remove.isPending}
                aria-label={t('passkeys.removeNamed', { name: key.name })}
                className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3 text-danger"
              >
                {t('passkeys.remove')}
              </button>
            </li>
          ))}
        </ul>
      ) : list.data ? (
        <p className="text-xs text-ink-muted">{t('passkeys.none')}</p>
      ) : null}

      {adding ? (
        <form
          className="flex flex-col gap-2 rounded-xl border border-line p-3"
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate({ password, name: name.trim() || t('passkeys.defaultName') });
          }}
        >
          <label htmlFor={nameId} className="flex flex-col gap-1">
            <span className="kw-label">{t('passkeys.name')}</span>
            <input
              id={nameId}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('passkeys.namePlaceholder')}
              maxLength={60}
              className="kw-field"
            />
          </label>
          <label htmlFor={passwordId} className="flex flex-col gap-1">
            <span className="kw-label">{t('passkeys.password')}</span>
            <PasswordInput
              id={passwordId}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="kw-field"
              required
            />
          </label>
          {addError ? (
            <p role="alert" className="text-sm text-danger">
              {addError}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={add.isPending || password.length === 0}
            className="kw-btn kw-btn-small"
          >
            {add.isPending ? t('passkeys.busy') : t('passkeys.confirm')}
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setPassword('');
              add.reset();
            }}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('nutrition.library.cancel')}
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('passkeys.add')}
        </button>
      )}
    </div>
  );
}

/** Alles, was der Server über dieses Konto weiß, als Datei (DSGVO Art. 15/20). */
function ExportData(): React.JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'failed'>('idle');

  return (
    <div className="flex flex-col gap-1 border-t border-line pt-3">
      <button
        type="button"
        disabled={state === 'busy'}
        onClick={() => {
          setState('busy');
          void exportAccountData()
            .then((outcome) => setState(outcome === 'cancelled' ? 'idle' : 'done'))
            .catch(() => setState('failed'));
        }}
        className="kw-btn kw-btn-quiet kw-btn-small"
      >
        {state === 'busy' ? t('settings.export.busy') : t('settings.export.action')}
      </button>
      <p
        role="status"
        className={`text-xs ${state === 'failed' ? 'text-danger' : 'text-ink-muted'}`}
      >
        {state === 'failed'
          ? t('settings.export.failed')
          : state === 'done'
            ? t('settings.export.done')
            : t('settings.export.hint')}
      </p>
    </div>
  );
}

/**
 * Konto endgültig löschen — mit Passwort, nicht mit einem Tipp.
 *
 * Was geteilt ist (Einkaufsliste, Vorrat), bleibt bei den anderen im Haushalt;
 * das sagt der Text vorher, nicht erst hinterher.
 */
function DeleteAccount(): React.JSX.Element {
  const { t } = useTranslation();
  const passwordId = useId();
  const remove = useDeleteAccount();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="kw-btn kw-btn-quiet kw-btn-small text-danger"
      >
        {t('settings.delete.action')}
      </button>
    );
  }

  const status = remove.error && 'status' in remove.error ? (remove.error.status as number) : null;

  return (
    <form
      className="flex flex-col gap-2 rounded-xl border border-danger p-3"
      onSubmit={(event) => {
        event.preventDefault();
        remove.mutate(password);
      }}
    >
      <p className="text-sm text-danger">{t('settings.delete.warning')}</p>
      <p className="text-xs text-ink-muted">{t('settings.delete.shared')}</p>
      <label htmlFor={passwordId} className="flex flex-col gap-1">
        <span className="kw-label">{t('settings.delete.password')}</span>
        <PasswordInput
          id={passwordId}
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className="kw-field"
          required
        />
      </label>
      {remove.isError ? (
        <p role="alert" className="text-sm text-danger">
          {status === 403 ? t('settings.delete.wrongPassword') : t('settings.delete.failed')}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={remove.isPending || password.length === 0}
        className="kw-btn kw-btn-small bg-danger"
      >
        {remove.isPending ? t('settings.delete.busy') : t('settings.delete.confirm')}
      </button>
      <button
        type="button"
        onClick={() => {
          setOpen(false);
          setPassword('');
          remove.reset();
        }}
        className="kw-btn kw-btn-quiet kw-btn-small"
      >
        {t('nutrition.library.cancel')}
      </button>
    </form>
  );
}

/** Erfolge (E13): Bestwerte, Ringe, Serien, Abzeichen — wer das nicht mag, schaltet ab. */
function AchievementsSetting({ child }: { child: boolean }): React.JSX.Element {
  const { t } = useTranslation();
  const [on, setOn] = useState<boolean | null>(null);
  const [share, setShare] = useState<boolean | null>(null);

  useEffect(() => {
    void readProfile(db).then((profile) => {
      setOn(profile.achievements);
      setShare(profile.shareWeekStats);
    });
  }, []);

  return (
    <section aria-labelledby="settings-achievements" className="kw-tile flex flex-col gap-2">
      <h3 id="settings-achievements" className="kw-kicker">
        {t('rings.settingsHeading')}
      </h3>
      <p className="text-sm text-ink-muted">
        {t(child ? 'rings.settingsIntroTeen' : 'rings.settingsIntro')}
      </p>
      {on === null ? null : (
        <button
          type="button"
          aria-pressed={on}
          onClick={() => {
            const next = !on;
            setOn(next);
            void updateAchievements(next, db);
          }}
          className={`kw-btn kw-btn-small ${on ? '' : 'kw-btn-quiet'}`}
        >
          {on ? t('rings.settingsOn') : t('rings.settingsOff')}
        </button>
      )}
      {/* Die Haushalts-Wochenzahl vergliche ein Kind mit Erwachsenen — nie für Kinder. */}
      {share === null || child ? null : (
        <>
          <p className="text-sm text-ink-muted">{t('householdGoal.settingsIntro')}</p>
          <button
            type="button"
            aria-pressed={share}
            onClick={() => {
              const next = !share;
              setShare(next);
              void updateShareWeekStats(next, db);
            }}
            className={`kw-btn kw-btn-small ${share ? '' : 'kw-btn-quiet'}`}
          >
            {share ? t('householdGoal.settingsOn') : t('householdGoal.settingsOff')}
          </button>
        </>
      )}
    </section>
  );
}

/**
 * Speicher auf dem Server: belegt von Kontingent, Einträge und Bilder getrennt
 * genannt. Ist er voll, bleiben neue Fotos auf dem Gerät — das steht hier, nicht
 * nur im Logbuch. Einträge gehen trotzdem immer durch.
 */
interface StorageReport {
  usedBytes: number;
  dataBytes: number;
  fileBytes: number;
  quotaBytes: number;
}

function StorageUsage(): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const full = useLiveQuery(() => readQuotaFull(db), [], false);
  const [usage, setUsage] = useState<StorageReport | null>(null);
  useEffect(() => {
    let alive = true;
    apiFetch<StorageReport>('/account/storage').then(
      (value) => alive && setUsage(value),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, [full]);
  if (!usage) return null;
  // Einträge sind klein — unter 1 MB in KB, sonst stünde dort „0 MB“.
  const size = (bytes: number) =>
    bytes >= 1024 ** 3
      ? `${(bytes / 1024 ** 3).toLocaleString(i18n.language, { maximumFractionDigits: 1 })} GB`
      : bytes >= 1024 ** 2
        ? `${(bytes / 1024 ** 2).toLocaleString(i18n.language, { maximumFractionDigits: bytes < 10 * 1024 ** 2 ? 1 : 0 })} MB`
        : `${Math.ceil(bytes / 1024).toLocaleString(i18n.language)} KB`;
  const share = Math.min(1, usage.usedBytes / usage.quotaBytes);
  return (
    <div className="flex flex-col gap-1 border-t border-line pt-2">
      <p className="text-sm">
        {t('storage.used', { used: size(usage.usedBytes), quota: size(usage.quotaBytes) })}
      </p>
      <p className="text-xs text-ink-muted">
        {t('storage.split', { data: size(usage.dataBytes), files: size(usage.fileBytes) })}
      </p>
      <span
        className="kw-bar"
        role="img"
        aria-label={t('storage.used', {
          used: size(usage.usedBytes),
          quota: size(usage.quotaBytes),
        })}
      >
        <i style={{ width: `${Math.round(share * 100)}%` }} />
      </span>
      {full ? (
        <p role="status" className="text-xs text-warn">
          {t('storage.full')}
        </p>
      ) : (
        <p className="text-xs text-ink-muted">{t('storage.why')}</p>
      )}
    </div>
  );
}
